import { InputLabel, Stack, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { SelectButton } from '@/shared/components/ui/button/select-button/select-button';
import { useDataTable } from '@/shared/components/ui/data-table/context/data-table-context';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { usePaginationQueryParams } from '@/shared/components/ui/pagination/hooks/use-pagination-query-params/use-pagination-query-params';
import {
  PaginationContainer,
  StyledPagination,
} from '@/shared/components/ui/pagination/pagination.styled';
import { PopoverController } from '@/shared/components/ui/popover/components/popover-controller/popover-controller';

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];
const LABEL_ID = 'pagination-select-label';

export const Pagination: React.FC = () => {
  const { t } = useTranslation();
  const { page, pageSize } = usePaginationQueryParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data } = useDataTable();
  const { items = [], pages, isLoading, isFetching } = data;

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', newPage.toString());
    setSearchParams(newParams, { replace: true });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageSize', newPageSize.toString());
    newParams.set('page', '1');
    setSearchParams(newParams, { replace: true });
  };

  // A remembered page can outlive its rows, and this control hides itself when
  // the list is empty -- leaving no way back.
  useEffect(() => {
    if (!pages || page <= pages) return;

    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', '1');
    setSearchParams(newParams, { replace: true });
  }, [page, pages, searchParams, setSearchParams]);

  if (!pages || pages === 0 || items.length === 0) {
    return null;
  }

  return (
    <PaginationContainer>
      <Stack direction="row" alignItems="center" gap="0.5rem">
        <InputLabel id={LABEL_ID}>
          <Typography variant="p8">
            {t('ui:assets_list.rows_per_page')}
          </Typography>
        </InputLabel>
        <PopoverController
          trigger={<SelectButton active>{pageSize}</SelectButton>}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          {(handleClose: () => void) => {
            return PAGE_SIZE_OPTIONS.map((option) => (
              <MenuItem
                key={option}
                onClick={() => {
                  handlePageSizeChange(option);
                  handleClose();
                }}
                selected={option === pageSize}
              >
                {option}
              </MenuItem>
            ));
          }}
        </PopoverController>
      </Stack>
      <StyledPagination
        disabled={isLoading || isFetching}
        count={pages}
        page={page}
        onChange={handlePageChange}
        siblingCount={0}
        boundaryCount={1}
      />
    </PaginationContainer>
  );
};
